import type { LedgerAccount, LedgerTransaction } from './types.ts';

/**
 * How a transaction counts in reports. Own transfers, settlement payments and balance
 * corrections are never income or spending (spec §36, §138, §141, §54A).
 */
export type FlowClass =
  | 'INCOME'
  | 'EXPENSE'
  | 'REFUND'
  | 'TRANSFER'
  | 'SAVINGS_CONTRIBUTION'
  | 'SAVINGS_WITHDRAWAL'
  | 'SETTLEMENT'
  | 'ADJUSTMENT'
  | 'IGNORED';

/**
 * A transfer is counted once, on the side of the account that is *not* a savings asset:
 * money leaving a current account for a savings/investment asset is a savings contribution;
 * money arriving from one is a withdrawal. The savings side of the same movement is neutral.
 */
export function classifyTransaction(
  transaction: LedgerTransaction,
  accounts: ReadonlyMap<string, LedgerAccount>,
): FlowClass {
  if (transaction.status === 'REVERSED' || transaction.status === 'CANCELLED') return 'IGNORED';
  switch (transaction.kind) {
    case 'INCOME':
      return 'INCOME';
    case 'EXPENSE':
      return 'EXPENSE';
    case 'REFUND':
      return 'REFUND';
    case 'SETTLEMENT_PAYMENT':
      return 'SETTLEMENT';
    case 'BALANCE_ADJUSTMENT':
      return 'ADJUSTMENT';
    case 'TRANSFER': {
      const source = accounts.get(transaction.accountId);
      const counterpart = transaction.counterpartAccountId
        ? accounts.get(transaction.counterpartAccountId)
        : undefined;
      if (!source || !counterpart) return 'TRANSFER';
      if (!source.isSavingsAsset && counterpart.isSavingsAsset) {
        return transaction.amountMinor < 0n ? 'SAVINGS_CONTRIBUTION' : 'SAVINGS_WITHDRAWAL';
      }
      return 'TRANSFER';
    }
  }
}
